import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import Reveal from "../components/ui/Reveal";
import SEO, { generateBreadcrumbSchema, generateFAQSchema } from "../components/seo/SEO";
import { useAppDownload } from "../context/AppDownloadContext";
import { usePricing } from "../hooks/usePricing";
import { useFaqs } from "../hooks/useFaqs";

const AUDIENCES = [
  { id: "preuni", label: "Pre-University" },
  { id: "university", label: "University" },
  { id: "institutions", label: "Institutions" },
];

function formatNaira(amount) {
  return "₦" + amount.toLocaleString("en-NG");
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "2027-05-07" -> "7 May 2027" */
function formatSeasonDate(iso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!match) return "";
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${Number(match[3])} ${month} ${match[1]}` : "";
}

const hasSeasonPrice = (plan) => (plan.season ?? 0) > 0;

function PricingCardSkeleton() {
  return (
    <div className="pricing-card" aria-hidden="true" style={{ opacity: 0.55 }}>
      <div className="pricing-skeleton-block" style={{ width: "40%", height: 22, marginBottom: "var(--space-3)", background: "var(--color-border)", borderRadius: 6 }} />
      <div className="pricing-skeleton-block" style={{ width: "60%", height: 40, marginBottom: "var(--space-2)", background: "var(--color-border)", borderRadius: 6 }} />
      <div className="pricing-skeleton-block" style={{ width: "90%", height: 14, marginBottom: "var(--space-5)", background: "var(--color-border)", borderRadius: 4 }} />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="pricing-skeleton-block" style={{ width: `${70 + (i % 3) * 10}%`, height: 13, marginBottom: "var(--space-2)", background: "var(--color-border)", borderRadius: 4 }} />
      ))}
      <div className="pricing-skeleton-block" style={{ width: "100%", height: 44, marginTop: "var(--space-5)", background: "var(--color-border)", borderRadius: 8 }} />
    </div>
  );
}

function PricingCard({ plan, billing, onDownload }) {
  const isInstitution = plan.price !== undefined;
  const isFree = isInstitution ? plan.price === "Free" : plan.monthly === 0;
  const isCustom = isInstitution && plan.price === "Custom";

  let displayPrice;
  let displayPeriod = null;

  if (isInstitution) {
    displayPrice = plan.price;
  } else if (isFree) {
    displayPrice = "₦0";
  } else if (billing === "season" && hasSeasonPrice(plan)) {
    displayPrice = formatNaira(plan.season);
    displayPeriod = " one-time";
  } else {
    const amount = billing === "yearly" ? plan.yearly : plan.monthly;
    displayPrice = formatNaira(amount);
    displayPeriod = billing === "yearly" ? "/yr" : "/mo";
  }

  const yearlySavings =
    !isInstitution && !isFree && billing === "yearly"
      ? plan.monthly * 12 - plan.yearly
      : 0;

  return (
    <div className={`pricing-card ${plan.featured ? "featured" : ""}`}>
      {plan.featured && <div className="pricing-badge">Most Popular</div>}
      <h3 className="heading-4">{plan.name}</h3>
      <div className="pricing-price">
        {displayPrice}
        {displayPeriod && <span>{displayPeriod}</span>}
      </div>
      {yearlySavings > 0 && (
        <div className="pricing-save-badge">
          Save {formatNaira(yearlySavings)} · 2 months free
        </div>
      )}
      {billing === "season" && hasSeasonPrice(plan) && plan.seasonEndsOn && (
        <div className="pricing-save-badge">
          Full access until {formatSeasonDate(plan.seasonEndsOn)}
        </div>
      )}
      <p className="body-small" style={{ marginTop: "var(--space-3)" }}>
        {plan.description}
      </p>
      <div className="pricing-features">
        {plan.features.map((feature) => (
          <div
            key={feature.text}
            className={`pricing-feature ${feature.included ? "" : "excluded"}`}
          >
            {feature.included ? (
              <Check size={18} strokeWidth={2.5} aria-hidden />
            ) : (
              <X size={18} strokeWidth={2.5} aria-hidden />
            )}
            {feature.text}
          </div>
        ))}
      </div>
      {plan.ctaType === "download" ? (
        <button
          type="button"
          className={`btn ${plan.featured ? "btn-primary" : "btn-secondary"}`}
          style={{ width: "100%" }}
          onClick={onDownload}
        >
          {plan.cta}
        </button>
      ) : (
        <Link
          to="/contact"
          className={`btn ${plan.featured ? "btn-primary" : "btn-secondary"}`}
          style={{ width: "100%" }}
        >
          {plan.cta}
        </Link>
      )}
      {isFree && plan.ctaType === "download" && (
        <p className="pricing-card-note body-small">No credit card required</p>
      )}
      {isCustom && (
        <p className="pricing-card-note body-small">Tailored quote  no surprise fees</p>
      )}
    </div>
  );
}

function FaqSkeleton() {
  return (
    <div className="pricing-faq-item" aria-hidden="true" style={{ opacity: 0.5 }}>
      <div style={{ width: "55%", height: 18, background: "var(--color-border)", borderRadius: 5, marginBottom: 10 }} />
      <div style={{ width: "90%", height: 13, background: "var(--color-border)", borderRadius: 4, marginBottom: 6 }} />
      <div style={{ width: "70%", height: 13, background: "var(--color-border)", borderRadius: 4 }} />
    </div>
  );
}

function PricingLoadError({ message, onRetry }) {
  return (
    <div className="pricing-load-error">
      <p className="body-base">{message}</p>
      <button type="button" className="btn btn-secondary" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function InstitutionPricing() {
  return (
    <div className="pricing-institution-panel">
      <Reveal>
        <div className="pricing-institution-card">
          <span className="eyebrow">Institutional partnerships</span>
          <h2 className="heading-3" style={{ marginTop: "var(--space-3)", marginBottom: "var(--space-4)" }}>
            Custom packages for universities and schools
          </h2>
          <p className="body-large" style={{ color: "var(--color-text-secondary)", maxWidth: 560, margin: "0 auto var(--space-6)" }}>
            Deploy CampusGenie across your institution with analytics, branding, and integrations tailored to your student population. Our team will put together a quote based on your needs.
          </p>
          <div className="cta-buttons" style={{ justifyContent: "center" }}>
            <Link to="/contact" className="btn btn-primary btn-lg">
              Contact Sales
            </Link>
            <Link to="/institutions" className="btn btn-secondary btn-lg">
              Learn More
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function AudiencePlans({ pricing, billing, onDownload }) {
  const { plans: allPlans, loading, error, refetch } = pricing;
  const plans = billing === "season" ? allPlans.filter(hasSeasonPrice) : allPlans;

  if (loading) {
    return (
      <div className="grid grid-3 pricing-plans-grid">
        {[0, 1, 2].map((i) => (
          <PricingCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error || plans.length === 0) {
    return (
      <PricingLoadError
        message={error || "No pricing plans are available right now."}
        onRetry={refetch}
      />
    );
  }

  return (
    <>
      <div className="grid grid-3 pricing-plans-grid">
        {plans.map((plan, index) => (
          <Reveal key={plan.id ?? plan.name} delay={index * 0.08}>
            <PricingCard plan={plan} billing={billing} onDownload={onDownload} />
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <p className="pricing-disclaimer body-small">
          Prices are shown in Nigerian Naira (₦), as they appear in the app.
          Campus ambassadors receive a complimentary Pro plan {" "}
          <Link to="/ambassador" className="legal-prose-link">
            learn more
          </Link>
          .
        </p>
      </Reveal>
    </>
  );
}

function PricingFaqs() {
  const { faqs, loading, error, refetch } = useFaqs({ category: "payment", pageSize: 20 });

  return (
    <section className="section section-alt">
      <div className="container pricing-faq-wrap">
        <Reveal>
          <div className="section-heading section-heading-center">
            <span className="eyebrow">FAQ</span>
            <h2 className="heading-2">Common pricing questions.</h2>
          </div>
        </Reveal>

        {loading && (
          <div className="pricing-faq-list">
            {[...Array(5)].map((_, i) => (
              <FaqSkeleton key={i} />
            ))}
          </div>
        )}

        {!loading && (error || faqs.length === 0) && (
          <PricingLoadError
            message={error || "No pricing FAQs are available right now."}
            onRetry={refetch}
          />
        )}

        {!loading && faqs.length > 0 && (
          <div className="pricing-faq-list">
            {faqs.map((faq, index) => (
              <Reveal key={faq.id ?? faq.question} delay={index * 0.06}>
                <div className="pricing-faq-item">
                  <h3 className="heading-4">{faq.question}</h3>
                  <p className="body-small">{faq.answer}</p>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function PricingPage() {
  const { openAppModal } = useAppDownload();
  const [audience, setAudience] = useState("preuni");
  const [billing, setBilling] = useState("monthly");
  const { faqs } = useFaqs({ category: "payment", pageSize: 20 });

  const isStudentAudience = audience !== "institutions";
  const pricing = usePricing(isStudentAudience ? audience : "");
  const seasonPlan = pricing.plans.find(hasSeasonPrice);

  useEffect(() => {
    if (billing === "season" && !seasonPlan) setBilling("monthly");
  }, [billing, seasonPlan]);

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Pricing", url: "/pricing" },
  ];

  const structuredData = useMemo(() => {
    const schemas = [generateBreadcrumbSchema(breadcrumbs)];

    if (faqs.length > 0) {
      schemas.push(
        generateFAQSchema(
          faqs.map((faq) => ({
            question: faq.question,
            answer: faq.answer,
          }))
        )
      );
    }

    return schemas;
  }, [faqs]);

  return (
    <>
      <SEO
        title="Pricing · Student & Institution Plans"
        description="CampusGenie pricing for students and institutions. Free, Scholar, and Pro plans for Nigerian students, pre-university and university. Custom institutional packages for universities and schools."
        url="/pricing"
        structuredData={structuredData}
      />

      <section className="pricing-hero">
        <div className="container">
          <Reveal>
            <span className="eyebrow">Pricing</span>
            <h1 className="heading-hero">Simple plans that grow with you.</h1>
            <p className="body-large pricing-hero-copy">
              Start free as a student, upgrade when you need more AI and campus tools, or partner with us as an institution.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <Reveal>
            <div className="pricing-audience-toggle" role="tablist" aria-label="Pricing audience">
              {AUDIENCES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={audience === item.id}
                  className={`pricing-audience-btn ${audience === item.id ? "active" : ""}`}
                  onClick={() => setAudience(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </Reveal>

          {isStudentAudience && (
            <Reveal delay={0.04}>
              <div className="pricing-billing-toggle" role="group" aria-label="Billing period">
                <button
                  type="button"
                  className={`pricing-billing-btn ${billing === "monthly" ? "active" : ""}`}
                  onClick={() => setBilling("monthly")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`pricing-billing-btn ${billing === "yearly" ? "active" : ""}`}
                  onClick={() => setBilling("yearly")}
                >
                  Yearly
                  <span className="pricing-billing-save">2 months free</span>
                </button>
                {seasonPlan && (
                  <button
                    type="button"
                    className={`pricing-billing-btn ${billing === "season" ? "active" : ""}`}
                    onClick={() => setBilling("season")}
                  >
                    Season Pass
                    <span className="pricing-billing-save">one-time</span>
                  </button>
                )}
              </div>
            </Reveal>
          )}

          <Reveal delay={0.05}>
            <p className="pricing-audience-copy body-large">
              {audience === "preuni" || audience === "university"
                ? billing === "season" && seasonPlan
                  ? `One payment covers you until ${formatSeasonDate(seasonPlan.seasonEndsOn)}. It does not renew automatically.`
                  : "Pay securely in the app. Plans don't renew automatically, so you're never charged unexpectedly."
                : "Flexible packages based on student count, branding, and integrations. Start free and scale."}
            </p>
          </Reveal>

          {isStudentAudience ? (
            <AudiencePlans
              pricing={pricing}
              billing={billing}
              onDownload={openAppModal}
            />
          ) : (
            <InstitutionPricing />
          )}
        </div>
      </section>

      <PricingFaqs />

      <section className="section">
        <div className="container">
          <Reveal>
            <div className="cta-section">
              <div className="cta-content">
                <h2 className="heading-2">Not sure which plan fits?</h2>
                <p className="body-large">
                  Download the app to explore the Free tier, or talk to our team about institutional partnerships.
                </p>
                <div className="cta-buttons">
                  <button type="button" className="btn btn-primary btn-lg" onClick={openAppModal}>
                    Download the App
                  </button>
                  <Link to="/contact" className="btn btn-secondary btn-lg">
                    Contact Sales
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
